import React, { useMemo } from 'react';

function PriceSparkline({
    priceChanges = [],
    color = '#34d399',
    width = 80,
    height = 24,
    direction = 'down'
}) {
    const { path, areaPath } = useMemo(() => {
        if (!priceChanges || priceChanges.length === 0) {
            return { path: '', areaPath: '' };
        }

        const points = priceChanges.map((pt) => ({
            t: new Date(pt.timestamp).getTime(),
            price: pt.price,
        }));

        const tMin = Math.min(...points.map((p) => p.t));
        const tMax = Math.max(...points.map((p) => p.t));
        const pMin = Math.min(...points.map((p) => p.price));
        const pMax = Math.max(...points.map((p) => p.price));

        const tRange = tMax - tMin || 1;
        const pRange = pMax - pMin || 1;

        const pad = 2;
        const innerW = width - pad * 2;
        const innerH = height - pad * 2;

        const xy = points.map((p) => {
            const x = pad + ((p.t - tMin) / tRange) * innerW;
            const y = pad + innerH - ((p.price - pMin) / pRange) * innerH;
            return { x, y };
        });

        const line = xy
            .map((pt, i) => `${i === 0 ? 'M' : 'L'} ${pt.x.toFixed(2)} ${pt.y.toFixed(2)}`)
            .join(' ');

        const area =
            line +
            ` L ${xy[xy.length - 1].x.toFixed(2)} ${(height - pad).toFixed(2)}` +
            ` L ${xy[0].x.toFixed(2)} ${(height - pad).toFixed(2)} Z`;

        return { path: line, areaPath: area };
    }, [priceChanges, width, height]);

    if (!path) {
        return (
            <div
                className="text-[9px] font-mono text-slate-700 flex items-center justify-center"
                style={{ width, height }}
            >
                —
            </div>
        );
    }

    const gradId = `sparkline-grad-${color.replace('#', '')}`;

    return (
        <svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            className="overflow-visible"
            style={{ color }}
        >
            <defs>
                <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity="0.3" />
                    <stop offset="100%" stopColor={color} stopOpacity="0" />
                </linearGradient>
            </defs>
            <path d={areaPath} fill={`url(#${gradId})`} />
            <path
                d={path}
                fill="none"
                stroke={color}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

export default PriceSparkline;
